/**
 * Card Actions Component
 * Renders user feedback action buttons on recommendation cards
 */

import { CardAction, CardActionConfig, CardActionData } from '../types';
import { StorageService } from '../services';
import { CARD_ACTION_COLORS } from '../design-system/colors';

/**
 * Tooltip styles for action buttons - injected once into the document
 */
const TOOLTIP_STYLES = `
  .card-action-btn {
    position: relative;
  }
  .card-action-btn .tooltip {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.375rem 0.625rem;
    background: #1f2937;
    color: #e5e7eb;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    border-radius: 0.375rem;
    border: 1px solid #4b5563;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0.15s ease;
    pointer-events: none;
    margin-bottom: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
    z-index: 50;
  }
  .card-action-btn .tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #4b5563;
  }
  .card-action-btn:hover .tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

let stylesInjected = false;

/**
 * Inject tooltip styles into the document head (once)
 */
function injectTooltipStyles(): void {
  if (stylesInjected) return;

  const styleElement = document.createElement('style');
  styleElement.setAttribute('data-component', 'card-actions');
  styleElement.textContent = TOOLTIP_STYLES;
  document.head.appendChild(styleElement);
  stylesInjected = true;
}

/**
 * SVG icons for action buttons
 */
const ACTION_ICONS = {
  'thumbs-up': `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
  </svg>`,
  'thumbs-down': `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
  </svg>`,
  'done': `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
  </svg>`,
  'not-applicable': `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>`
} as const;

/**
 * Action button configurations
 */
const ACTION_CONFIGS: CardActionConfig[] = [
  {
    action: 'thumbs-up',
    title: 'Helpful / Relevant',
    activeClass: CARD_ACTION_COLORS.thumbsUp.active,
    icon: ACTION_ICONS['thumbs-up']
  },
  {
    action: 'thumbs-down',
    title: 'Not relevant',
    activeClass: CARD_ACTION_COLORS.thumbsDown.active,
    icon: ACTION_ICONS['thumbs-down']
  },
  {
    action: 'done',
    title: 'Already implemented',
    activeClass: CARD_ACTION_COLORS.done.active,
    icon: ACTION_ICONS['done']
  },
  {
    action: 'not-applicable',
    title: 'Not applicable',
    activeClass: CARD_ACTION_COLORS.notApplicable.active,
    icon: ACTION_ICONS['not-applicable']
  }
];

/**
 * Callback type for action changes
 */
export type CardActionChangeCallback = (hasActions: boolean) => void;

/**
 * Component for rendering and managing card action buttons
 */
export class CardActionsComponent {
  private storageService: StorageService;
  private onChangeCallbacks: CardActionChangeCallback[] = [];

  constructor() {
    this.storageService = new StorageService();
    injectTooltipStyles();
  }

  /**
   * Register a callback for when actions change
   * @param callback - Function to call when actions are added/removed
   */
  onChange(callback: CardActionChangeCallback): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * Notify all callbacks of action changes
   */
  private notifyChange(): void {
    const hasActions = this.storageService.hasActions();
    this.onChangeCallbacks.forEach(callback => callback(hasActions));
  }

  /**
   * Render the action buttons HTML
   * @param checkKey - Unique identifier for the check
   * @param deviceType - Device type (mobile, desktop, both)
   * @returns HTML string for action buttons
   */
  render(checkKey: string, deviceType: string): string {
    const savedAction = this.storageService.getAction(checkKey, deviceType);

    const buttons = ACTION_CONFIGS.map(config => {
      const isActive = savedAction?.action === config.action;
      const activeClasses = isActive ? `${config.activeClass} ring-2 ring-offset-1 ring-offset-gray-800` : '';

      return `
        <button
          class="card-action-btn flex items-center justify-center w-8 h-8 rounded-md border border-gray-600 bg-gray-700/50 hover:bg-gray-600 transition-all duration-200 ${activeClasses}"
          data-action="${config.action}"
          data-check-key="${checkKey}"
          data-device-type="${deviceType}"
          aria-label="${config.title}"
          aria-pressed="${isActive}"
        >
          <span class="tooltip">${config.title}</span>
          ${config.icon}
        </button>
      `;
    }).join('');

    return `
      <div class="card-actions flex items-center gap-2 mt-4 pt-4 border-t border-gray-700/50">
        <span class="text-xs text-gray-500 mr-2">Your feedback:</span>
        ${buttons}
      </div>
    `;
  }

  /**
   * Attach click handlers to action buttons within a container
   * @param container - Parent element containing action buttons
   * @param getCardData - Function to retrieve card data from a button
   */
  attachHandlers(
    container: HTMLElement,
    getCardData: (button: HTMLButtonElement) => { area: string; recommendation: string }
  ): void {
    const buttons = container.querySelectorAll<HTMLButtonElement>('.card-action-btn');

    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleActionClick(button, getCardData);
      });
    });
  }

  /**
   * Handle action button click
   * @param button - The clicked button element
   * @param getCardData - Function to retrieve card data
   */
  private handleActionClick(
    button: HTMLButtonElement,
    getCardData: (button: HTMLButtonElement) => { area: string; recommendation: string }
  ): void {
    const action = button.dataset.action as CardAction;
    const checkKey = button.dataset.checkKey || '';
    const deviceType = button.dataset.deviceType || '';

    if (!action || !checkKey) return;

    const savedAction = this.storageService.getAction(checkKey, deviceType);
    const actionsContainer = button.closest('.card-actions');

    // If clicking the same action, toggle it off
    if (savedAction?.action === action) {
      this.storageService.removeAction(checkKey, deviceType);
      this.updateButtonStates(actionsContainer, null);
    } else {
      // Get card data for storage
      const cardData = getCardData(button);

      const actionData: CardActionData = {
        checkKey,
        area: cardData.area,
        deviceType,
        recommendation: cardData.recommendation,
        action,
        timestamp: Date.now()
      };

      this.storageService.saveAction(actionData);
      this.updateButtonStates(actionsContainer, action);
    }

    this.notifyChange();
  }

  /**
   * Update visual states of all action buttons in a container
   * @param container - Container element with action buttons
   * @param activeAction - Currently active action, or null if none
   */
  private updateButtonStates(container: Element | null, activeAction: CardAction | null): void {
    if (!container) return;

    const buttons = container.querySelectorAll<HTMLButtonElement>('.card-action-btn');

    buttons.forEach(btn => {
      const btnAction = btn.dataset.action as CardAction;
      const config = ACTION_CONFIGS.find(c => c.action === btnAction);

      if (!config) return;

      const isActive = btnAction === activeAction;

      // Remove all active classes first
      ACTION_CONFIGS.forEach(c => {
        btn.classList.remove(...c.activeClass.split(' '));
      });
      btn.classList.remove('ring-2', 'ring-offset-1', 'ring-offset-gray-800');

      // Add active classes if this is the active action
      if (isActive) {
        btn.classList.add(...config.activeClass.split(' '), 'ring-2', 'ring-offset-1', 'ring-offset-gray-800');
      }

      btn.setAttribute('aria-pressed', isActive.toString());
    });
  }

  /**
   * Restore action states for all cards in a container
   * Called after cards are rendered to restore saved states
   * @param container - Container element with cards
   * @param getCardData - Function to retrieve card data from a button
   */
  restoreStates(
    container: HTMLElement,
    getCardData: (button: HTMLButtonElement) => { area: string; recommendation: string }
  ): void {
    this.attachHandlers(container, getCardData);
  }

  /**
   * Check if any actions have been saved
   * @returns True if at least one action exists
   */
  hasActions(): boolean {
    return this.storageService.hasActions();
  }

  /**
   * Get the storage service instance
   * @returns Storage service
   */
  getStorageService(): StorageService {
    return this.storageService;
  }
}
